import React, { useEffect, useState, useMemo, useCallback } from 'react'
import { useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import pdfMake from 'pdfmake'
import Div from '@jumbo/shared/Div'
import { Pagination, Stack } from '@mui/material'
import Typography from '@mui/material/Typography'
import { LoadingButton } from '@mui/lab'
import FilterAltIcon from '@mui/icons-material/FilterAlt'
import DownloadIcon from '@mui/icons-material/Download'
import CircularProgress from '@mui/material/CircularProgress'
import { useSnackbar } from 'notistack'
import vfs from '../../../../../../../fonts/NimbusSanL/nimbus-san'
import EnrolmentListReportFilter from './EnrolmentListReportFilter'
import { buildCsv, downloadCsv } from 'app/utils/reports/csvExport'
import '../../../../../../pages/reports/reportstyles.css'

const EnrolmentListReportLayout = ({
    imageData,
    viewMutation,
    pdfMutation,
    pageMutation,
    apiCallInProgress,
    setApiCallInProgress,
    pdfData,
}) => {
    const { enrolmentListReport, loading, error } = useSelector((state) => state.reports)
    const { enqueueSnackbar } = useSnackbar()
    const navigate = useNavigate()
    const [isFilterVisible, setFilterVisible] = useState(false)
    const [currentPage, setCurrentPage] = useState(1)
    const [pdfDownloading, setPdfDownloading] = useState(false)
    const [pdfTemplate, setpdfTemplate] = useState({})
    const itemsPerPage = 25
    const totalPages = Math.ceil(enrolmentListReport.totalCount / itemsPerPage)

    useEffect(() => {
        if (!apiCallInProgress || loading) return
        setApiCallInProgress(false)

        if (error) handleError(error)
    }, [loading])

    const handleError = useCallback(
        (error) => {
            if (error.status === '401') {
                navigate('/profile/signout')
            } else if (error.detail) {
                enqueueSnackbar(error.detail, {
                    variant: 'error',
                    anchorOrigin: {
                        vertical: 'top',
                        horizontal: 'right',
                    },
                })
            } else {
                enqueueSnackbar(error, {
                    variant: 'error',
                    anchorOrigin: {
                        vertical: 'top',
                        horizontal: 'right',
                    },
                })
            }
        },
        [enqueueSnackbar, navigate]
    )

    pdfMake.vfs = vfs
    pdfMake.fonts = {
        NimbusSans: {
            normal: 'NimbusSanL-Reg.otf',
            bold: 'NimbusSanL-Bol.otf',
            italics: 'NimbusSanL-RegIta.otf',
            bolditalics: 'NimbusSanL-BolIta.otf',
        },
    }

    const handlePageChange = async (page) => {
        try {
            await pageMutation.mutate(page)
            setCurrentPage(page)
        } catch (error) {
            console.error('Error updating page:', error)
        }
    }

    const handleFilterClick = () => {
        setFilterVisible(!isFilterVisible)
    }

    const headers = React.useMemo(
        () => ['Customer ', 'Phone No', 'Email', 'Family ', 'Participant', 'Term', 'Class', 'Section'],
        []
    )

    const parseToPdfData = useMemo(
        () => (data) => {
            return data.map((d) => {
                const fullCustomerName = d ? `${d.firstName ?? ''} ${d.lastName ?? ''}` : ''
                const fullParticipantName = d ? `${d.memberFirstName ?? ''} ${d.memberLastName ?? ''}` : ''
                return [
                    { text: fullCustomerName, style: 'leftAlign' },
                    { text: d.phoneNumber, style: 'leftAlign' },
                    { text: d.email, style: 'leftAlign' },
                    { text: d.familyName, style: 'leftAlign' },
                    { text: fullParticipantName, style: 'leftAlign' },
                    { text: d.termName, style: 'cell' },
                    { text: d.className, style: 'leftAlign' },
                    { text: d.sectionName, style: 'leftAlign' },
                ]
            })
        },
        []
    )

    const pageRow = []

    const tableBody =
        pdfData?.data?.length > 0
            ? parseToPdfData(pdfData.data).map((row) =>
                  row.map((cell, index) => ({
                      text: cell,
                      style: ['cell', index == 4 ? 'center' : 'leftAlign'],
                  }))
              )
            : []

    const pdfTemplateDefault = React.useMemo(() => {
        return {
            pageSize: 'A4',
            pageOrientation: 'landscape',
            pageMargins: [10, 45, 15, 45],
            footer: function (currentPage, pageCount) {
                const isLastPage = currentPage === pageCount
                if (!isLastPage) {
                    return {
                        columns: [
                            {
                                table: {
                                    headerRows: 0,
                                    widths: [240, 80, 120, 100, 110, 110, 75, 115],
                                    body: [pageRow.map((cell) => ({ text: cell.text, style: cell.style }))],
                                },
                                margin: [15, 10, 15, 10],
                            },
                        ],
                    }
                }

                return {
                    columns: [
                        {
                            table: {
                                headerRows: 0,
                                widths: [240, 80, 120, 100, 110, 110, 75, 115],
                                body: [pageRow.map((cell) => ({ text: cell.text, style: cell.style }))],
                            },
                            margin: [15, 5, 15, 20],
                        },
                    ],
                }
            },
            content: [
                {
                    image: imageData,
                    width: 70,
                },
                {
                    text: 'Enrolment List  Report',
                    style: 'header',
                    alignment: 'center',
                },
                {
                    layout: {
                        fillColor: function (rowIndex) {
                            return rowIndex % 2 === 1 ? '#f2f2f2' : null
                        },
                    },
                    style: 'withMargin',
                    alignment: 'center',
                    table: {
                        headerRows: 1,
                        widths: [100, 70, 125, 80, 95, 90, 110, 70],
                        body: [
                            headers.map((header, index) => {
                                return { text: header, style: 'headerRow' }
                            }),
                            ...tableBody,
                        ],
                    },
                },
            ],
            defaultStyle: {
                font: 'NimbusSans',
            },
            styles: {
                withMargin: {
                    margin: [5, 20, 20, 5],
                },
                header: {
                    fontSize: 18,
                    bold: true,
                },
                headerRow: {
                    fillColor: '#ADD8E6',
                    bold: true,
                    fontSize: 10,
                },
                cell: {
                    fontSize: 9,
                    alignment: 'center',
                },
                rightAlign: {
                    alignment: 'right',
                    fontSize: 9,
                },
                leftAlign: {
                    alignment: 'left',
                    fontSize: 9,
                },
                rowHeading: {
                    alignment: 'center',
                    fontSize: 10,
                },
            },
        }
    }, [imageData, headers, pdfData])

    useEffect(() => {
        const template = { ...pdfTemplateDefault }
        setpdfTemplate(template)
    }, [headers, pdfData, pdfTemplateDefault])

    const create = async () => {
        await pdfMutation.mutate()
        setPdfDownloading(true)
    }

    const createCsv = async () => {
        try {
            const data = await pdfMutation.mutateAsync()
            const pdfRows = data?.data?.length ? parseToPdfData(data.data) : []
            const rows = pdfRows.map((row) => row.map((cell) => (typeof cell === 'object' && cell?.text != null ? cell.text : cell)))
            const csvString = buildCsv(headers, rows)
            const today = new Date()
            downloadCsv(csvString, `Enrolment_List_Report_${today.toLocaleDateString()}_${today.toLocaleTimeString()}.csv`)
        } catch (err) {
            if (err?.message) handleError(err)
        }
    }

    useEffect(() => {
        if (!pdfDownloading) return
        setPdfDownloading(false)
        const pdfDocGenerator = pdfMake.createPdf(pdfTemplate)
        let today = new Date()
        pdfDocGenerator.download(`Enrolment_List_Report_${today.toLocaleDateString()}_${today.toLocaleTimeString()}`)
    }, [pdfTemplate])

    return (
        <Div>
            <Div>
                <h1 className='report-heading'>Enrolment List Report</h1>
                <Div
                    className='report-loading-button'
                    sx={{
                        display: 'flex',
                        justifyContent: 'flex-end',
                        alignItems: 'center',
                        gap: 1,
                        marginTop: '-55px',
                        flexWrap: 'wrap',
                    }}>
                    <LoadingButton
                        onClick={create}
                        size={'small'}
                        type='submit'
                        variant={'contained'}
                        disabled={pdfMutation.isPending}>
                        <DownloadIcon sx={{ fontSize: 'medium', marginRight: '2px' }} /> Download Report
                    </LoadingButton>
                    <LoadingButton
                        onClick={createCsv}
                        size={'small'}
                        type='button'
                        variant={'contained'}
                        disabled={pdfMutation.isPending}>
                        <DownloadIcon sx={{ fontSize: 'medium', marginRight: '2px' }} /> Download CSV
                    </LoadingButton>
                    <LoadingButton onClick={handleFilterClick} size={'small'} type='submit' variant={'contained'}>
                        <FilterAltIcon fontSize='medium' />
                    </LoadingButton>
                </Div>
            </Div>
            {isFilterVisible && <EnrolmentListReportFilter mutation={viewMutation} />}
            <>
                <div className='report-container'>
                    {loading && (
                        <CircularProgress
                            sx={{
                                position: 'absolute',
                                top: '50%',
                                left: '50%',
                                transform: 'translate(-50%, -50%)',
                            }}
                        />
                    )}
                    {!loading && enrolmentListReport.totalCount === 0 ? (
                        <Typography variant={'primary'}>No data. Select suitable filter to view data</Typography>
                    ) : (
                        <div className='custom-table'>
                            <table>
                                <tbody>
                                    <tr>
                                        {headers.map((h, index) => (
                                            <th key={h}>{h}</th>
                                        ))}
                                    </tr>
                                </tbody>
                                <tbody>
                                    {enrolmentListReport.data?.map((d, index) => {
                                        const fullCustomerName = d ? `${d.firstName ?? ''} ${d.lastName ?? ''}` : ''

                                        const fullParticipantName = d
                                            ? `${d.memberFirstName ?? ''} ${d.memberLastName ?? ''}`
                                            : ''

                                        return (
                                            <tr key={index}>
                                                <td style={{ textAlign: 'left', width: '140px' }}>
                                                    {fullCustomerName}
                                                </td>
                                                <td style={{ textAlign: 'left', width: '110px' }}>{d?.phoneNumber}</td>
                                                <td style={{ textAlign: 'left', width: '100px' }}>{d?.email}</td>
                                                <td style={{ textAlign: 'left', width: '120px' }}>{d?.familyName}</td>
                                                <td style={{ textAlign: 'left', width: '170px' }}>
                                                    {fullParticipantName}
                                                </td>
                                                <td style={{ textAlign: 'center', width: '120px' }}>{d?.termName}</td>
                                                <td style={{ textAlign: 'left', width: '180px' }}>{d?.className}</td>
                                                <td style={{ textAlign: 'left', width: '140px' }}>{d?.sectionName}</td>
                                            </tr>
                                        )
                                    })}
                                    <tr>
                                        <td colSpan={headers.length} />
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    )}
                    <Stack>
                        <Pagination
                            count={Math.ceil(enrolmentListReport.totalCount / itemsPerPage)}
                            onChange={(event, page) => {
                                handlePageChange(page)
                            }}
                            page={currentPage}
                            siblingCount={1}
                            boundaryCount={1}
                            sx={{ display: 'flex', justifyContent: 'flex-end', marginTop: '5px', marginBottom: '5px' }}
                            variant='outlined'
                        />
                    </Stack>
                </div>
            </>
        </Div>
    )
}

export default EnrolmentListReportLayout
